import { DndContext, pointerWithin, useSensor, useSensors, PointerSensor, type DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { Settings, Sparkles } from 'lucide-react'
import { useState, useRef } from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import type { Dashboard, DashboardAnalyticsResponse, DashboardWidget } from '@/types/api/dashboard'
import { DashboardWidgetType } from '@/types/enums'

import { DashboardEditMode } from './DashboardEditMode'
import { DashboardGrid } from './DashboardGrid'
import { DashboardSaveButton } from './DashboardSaveButton'
import { DashboardSettingsDialog } from './DashboardSettingsDialog'
import { DashboardWidgetPicker } from './DashboardWidgetPicker'
import { DashboardWidgetSettingsDialog } from './DashboardWidgetSettingsDialog'

interface DashboardBoardProps {
  headerTitle: React.ReactNode
  headerDescription?: string
  dashboard?: Dashboard
  widgets: DashboardWidget[]
  widgetData: Record<string, DashboardAnalyticsResponse | undefined>
  isLoading: boolean
  isSaving: boolean
  lockedRecordGroupId?: string
  onCreateWidget: (widget: Omit<DashboardWidget, 'id' | 'createdAt' | 'updatedAt' | 'dashboardId'> & { tempId?: string }) => void
  onDeleteWidget: (widgetId: string) => void
  onResizeWidget: (widgetId: string, w: number, h: number) => void
  onReorderWidgets?: (widgetIds: string[]) => void
  onWidgetsChange: (widgets: DashboardWidget[]) => void
  onLoadMore?: (widgetId: string, dataSource: string, config: Record<string, unknown>) => Promise<void>
  onSaveSettings: (updates: { title: string; description: string | null; isDefault: boolean }) => void
  /** Whether the user may change this dashboard (settings, layout, widgets). Defaults to true. */
  canEdit?: boolean
}

export function DashboardBoard({
  headerTitle,
  headerDescription,
  dashboard,
  widgets,
  widgetData,
  isLoading,
  isSaving,
  lockedRecordGroupId,
  onCreateWidget,
  onDeleteWidget,
  onResizeWidget,
  onReorderWidgets,
  onWidgetsChange,
  onLoadMore,
  onSaveSettings,
  canEdit = true,
}: DashboardBoardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [pendingSettingsWidget, setPendingSettingsWidget] = useState<DashboardWidget | null>(null)
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [paletteDragWidget, setPaletteDragWidget] = useState<DashboardWidget | null>(null)
  const boardRef = useRef<HTMLDivElement>(null)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const handleStartAddWidget = (type: DashboardWidgetType, addToCanvas: boolean = false) => {
    let defaultW = 4
    let defaultH = 4
    if (type === DashboardWidgetType.NUMBER_CARD || type === DashboardWidgetType.STATUS_BADGE_GROUP) {
      defaultW = 3
      defaultH = 2
    } else if (type === DashboardWidgetType.PROGRESS_BAR) {
      defaultW = 3
      defaultH = 4
    } else if (type === DashboardWidgetType.NUMBER_CARD_GROUP) {
      defaultW = 12
      defaultH = 2
    }

    const tempWidget: DashboardWidget = {
      id: `temp-${crypto.randomUUID()}`,
      dashboardId: dashboard?.id ?? null,
      type,
      title: `New ${type.replaceAll('_', ' ')}`,
      x: 0,
      y: 0,
      w: defaultW,
      h: defaultH,
      sortOrder: widgets.length,
      dataSource: '',
      dataSourceConfig: lockedRecordGroupId ? { recordGroupIds: [lockedRecordGroupId] } : {},
      chartConfig: {},
      config: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    if (addToCanvas) {
      onWidgetsChange([...widgets, tempWidget])
    }
    setPendingSettingsWidget(tempWidget)
  }

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id.toString()
    setActiveDragId(id)

    if (event.active.data.current?.source === 'dashboard-palette') {
      const widgetType = event.active.data.current?.widgetType as DashboardWidgetType
      let defaultW = 4
      let defaultH = 4
      if (widgetType === DashboardWidgetType.NUMBER_CARD || widgetType === DashboardWidgetType.STATUS_BADGE_GROUP) {
        defaultW = 3
        defaultH = 2
      } else if (widgetType === DashboardWidgetType.PROGRESS_BAR) {
        defaultW = 3
        defaultH = 3
      } else if (widgetType === DashboardWidgetType.NUMBER_CARD_GROUP) {
        defaultW = 12
        defaultH = 2
      }
      setPaletteDragWidget({
        id: id,
        dashboardId: dashboard?.id ?? null,
        type: widgetType,
        title: `New ${widgetType.replaceAll('_', ' ')}`,
        x: 0,
        y: 0,
        w: defaultW,
        h: defaultH,
        sortOrder: widgets.length,
        dataSource: '',
          dataSourceConfig: lockedRecordGroupId ? { recordGroupIds: [lockedRecordGroupId] } : {},
        chartConfig: {},
        config: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null)
    setPaletteDragWidget(null)
    const { active, over } = event
    if (!over) return

    if (active.data.current?.source === 'dashboard-palette') {
      const widgetType = active.data.current?.widgetType as DashboardWidgetType
      if (widgetType) handleStartAddWidget(widgetType, true)
      return
    }

    if (active.id !== over.id) {
      const oldIndex = widgets.findIndex((w) => w.id === active.id)
      const newIndex = widgets.findIndex((w) => w.id === over.id)
      
      const newWidgets = arrayMove(widgets, oldIndex, newIndex)
      const updatedWidgets = newWidgets.map((w, index) => ({ ...w, sortOrder: index }))
      onWidgetsChange(updatedWidgets)
      onReorderWidgets?.(updatedWidgets.map(w => w.id))
    }
  }

  const handleDeleteWidget = (widgetId: string) => {
    onWidgetsChange(widgets.filter((widget) => widget.id !== widgetId))
    onDeleteWidget(widgetId)
  }

  const handleWidgetUpdate = (widgetId: string, updates: Partial<DashboardWidget>) => {
    onWidgetsChange(widgets.map((widget) => (widget.id === widgetId ? { ...widget, ...updates } : widget)))
  }

  const handleSaveNewWidget = (updates: Partial<DashboardWidget>) => {
    if (!pendingSettingsWidget) return
    
    const tempId = pendingSettingsWidget.id
    
    const newWidgetData = {
      type: pendingSettingsWidget.type,
      title: updates.title ?? pendingSettingsWidget.title,
      x: pendingSettingsWidget.x,
      y: pendingSettingsWidget.y,
      w: pendingSettingsWidget.w,
      h: pendingSettingsWidget.h,
      sortOrder: pendingSettingsWidget.sortOrder,
      dataSource: updates.dataSource ?? pendingSettingsWidget.dataSource,
      dataSourceConfig: updates.dataSourceConfig ?? pendingSettingsWidget.dataSourceConfig,
      chartConfig: updates.chartConfig ?? pendingSettingsWidget.chartConfig,
      config: updates.config ?? pendingSettingsWidget.config,
      tempId,
    }
    
    onCreateWidget(newWidgetData)
    setPendingSettingsWidget(null)
  }

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div ref={boardRef} className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl border border-primary-100/50 bg-gradient-to-br from-primary-50 via-white to-indigo-50/50 p-6 shadow-sm">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(99,102,241,0.06),transparent_70%)] pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(16,185,129,0.04),transparent_70%)] pointer-events-none" />
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between relative z-10">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-sm">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                {headerTitle}
              </div>
              {headerDescription ? (
                <p className="ml-9 text-sm text-neutral-500">{headerDescription}</p>
              ) : null}
            </div>
            {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSettingsOpen(true)}
                title="Dashboard settings"
                className="border-primary-200/50 hover:border-primary-300 hover:bg-primary-50/50 shadow-sm"
              >
                <Settings className="h-4 w-4 text-primary-600" />
              </Button>
              <DashboardEditMode
                isEditing={isEditing}
                onStart={() => setIsEditing(true)}
                onDone={() => setIsEditing(false)}
                onCancel={() => {
                  setIsEditing(false)
                  if (dashboard?.widgets) onWidgetsChange(dashboard.widgets)
                }}
              />
              <DashboardSaveButton
                onSave={() => setIsEditing(false)}
                disabled={!isEditing || !dashboard || isSaving}
                isSaving={isSaving}
              />
            </div>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="dashboard-grid-bg rounded-2xl p-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-[280px] rounded-xl bg-white/60" />
              ))}
            </div>
          </div>
        ) : (
          <div className="dashboard-grid-bg rounded-2xl p-4 sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
              {isEditing ? <DashboardWidgetPicker onAddWidget={handleStartAddWidget} /> : null}
              <div className="min-w-0 flex-1">
                <DashboardGrid
                  widgets={widgets}
                  widgetData={widgetData}
                  isEditing={isEditing}
                  lockedRecordGroupId={lockedRecordGroupId}
                  onDeleteWidget={handleDeleteWidget}
                  onWidgetUpdate={handleWidgetUpdate}
                  onResizeWidget={onResizeWidget}
                  onLoadMore={onLoadMore}
                  activeDragId={activeDragId}
                  paletteDragWidget={paletteDragWidget}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <DashboardSettingsDialog
        open={settingsOpen}
        dashboard={dashboard}
        onOpenChange={setSettingsOpen}
        onSave={onSaveSettings}
      />
      
      <DashboardWidgetSettingsDialog
        open={!!pendingSettingsWidget}
        widget={pendingSettingsWidget}
        lockedRecordGroupId={lockedRecordGroupId}
        isNewWidget={true}
        onOpenChange={(open) => !open && setPendingSettingsWidget(null)}
        onSave={handleSaveNewWidget}
      />
    </DndContext>
  )
}
